import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useBranch } from '../context/BranchContext';
import Sidebar from '../components/layout/Sidebar';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from '../lib/toast';
import { 
  Plus, X, Trash2, Edit2, DollarSign, TrendingUp, TrendingDown, 
  Calendar, Users, Zap, Building2, Wrench, Megaphone, Shield, 
  MoreHorizontal, CreditCard, Banknote, ArrowUpDown, CheckCircle,
  AlertTriangle, ChevronLeft, ChevronRight, Wallet, Store, PiggyBank
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';

const CATEGORY_ICONS = {
  nomina: Users,
  servicios: Zap,
  renta: Building2,
  mantenimiento: Wrench,
  marketing: Megaphone,
  seguros: Shield,
  otros: MoreHorizontal
};

const PAYMENT_METHODS = [
  { id: 'efectivo', name: 'Efectivo', icon: Banknote },
  { id: 'transferencia', name: 'Transferencia', icon: ArrowUpDown },
  { id: 'tarjeta', name: 'Tarjeta', icon: CreditCard }
];

const MONEY_SOURCES = [
  { id: 'caja_chica', name: 'Caja Chica', icon: PiggyBank, description: 'Fondo para gastos pequeños' },
  { id: 'caja_ventas', name: 'Caja de Ventas', icon: Store, description: 'Efectivo de ventas del día' }
];

const Egresos = () => {
  const { api } = useAuth();
  const { currentBranch, isMatriz } = useBranch();
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [egresos, setEgresos] = useState([]);
  const [summary, setSummary] = useState(null);
  const [cajaChica, setCajaChica] = useState({ balance: 0 });
  const [cajaVentas, setCajaVentas] = useState({ balance: 0, isOpen: false });
  
  // Current month/year for view
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth() + 1);
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  
  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [showAddCajaChicaModal, setShowAddCajaChicaModal] = useState(false);
  const [addCajaChicaAmount, setAddCajaChicaAmount] = useState('');
  const [addCajaChicaSource, setAddCajaChicaSource] = useState('manual');
  const [editingEgreso, setEditingEgreso] = useState(null);
  const [form, setForm] = useState({
    category: '',
    subcategory: '',
    description: '',
    amount: '',
    payment_date: new Date().toISOString().split('T')[0],
    payment_method: 'efectivo',
    money_source: 'caja_chica',
    reference: '',
    notes: ''
  });

  useEffect(() => {
    fetchData();
  }, [currentMonth, currentYear, currentBranch]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Build branch filter
      const branchParams = currentBranch 
        ? `&branch_id=${currentBranch.branch_id}&is_matriz=${isMatriz}` 
        : '';
      
      const [catRes, summaryRes, cajaChicaRes] = await Promise.all([
        api.get('/finances/egresos/categories'),
        api.get(`/finances/egresos-summary?month=${currentMonth}&year=${currentYear}${branchParams}`),
        api.get(`/finances/caja-chica${branchParams ? '?' + branchParams.substring(1) : ''}`)
      ]);
      setCategories(catRes.data);
      setSummary(summaryRes.data);
      setEgresos(summaryRes.data.recent_egresos || []);
      setCajaChica(cajaChicaRes.data);
      
      // Try to get caja ventas status
      try {
        const cajaVentasRes = await api.get(`/cash-register/current${branchParams ? '?' + branchParams.substring(1) : ''}`);
        const breakdown = cajaVentasRes.data.cash_out_breakdown || {};
        setCajaVentas({ 
          balance: cajaVentasRes.data.available_cash || 0,
          cashSales: cajaVentasRes.data.cash_sales || 0,
          initialAmount: cajaVentasRes.data.initial_amount || 0,
          totalCashOut: cajaVentasRes.data.total_cash_out || 0,
          compras: breakdown.compras || 0,
          gastosOperativos: breakdown.gastos_operativos || 0,
          otrosSalidas: breakdown.otros || 0,
          isOpen: true 
        });
      } catch {
        setCajaVentas({ balance: 0, isOpen: false });
      }
    } catch (error) {
      console.error(error);
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setForm({
      category: '',
      subcategory: '',
      description: '',
      amount: '',
      payment_date: new Date().toISOString().split('T')[0],
      payment_method: 'efectivo',
      money_source: 'caja_chica',
      reference: '',
      notes: ''
    });
    setEditingEgreso(null);
  };

  const openModal = (egreso = null) => {
    if (egreso) {
      setEditingEgreso(egreso);
      setForm({
        category: egreso.category || '',
        subcategory: egreso.subcategory || '',
        description: egreso.description || '',
        amount: egreso.amount?.toString() || '',
        payment_date: egreso.payment_date || new Date().toISOString().split('T')[0],
        payment_method: egreso.payment_method || 'efectivo',
        money_source: egreso.money_source || 'caja_chica',
        reference: egreso.reference || '',
        notes: egreso.notes || ''
      });
    } else {
      resetForm();
    }
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.category || !form.description || !form.amount) {
      return toast.error('Completa los campos obligatorios');
    }

    try {
      const data = {
        ...form,
        amount: parseFloat(form.amount)
      };

      if (editingEgreso) {
        await api.put(`/finances/egresos/${editingEgreso.egreso_id}`, data);
        toast.success('Egreso actualizado');
      } else {
        await api.post('/finances/egresos', data);
        toast.success('Egreso registrado');
      }

      setShowModal(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error('Error al guardar egreso');
    }
  };

  const handleDelete = async (egresoId) => {
    if (!window.confirm('¿Eliminar este egreso?')) return;
    try {
      await api.delete(`/finances/egresos/${egresoId}`);
      toast.success('Egreso eliminado');
      fetchData();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const changeMonth = (delta) => {
    let newMonth = currentMonth + delta;
    let newYear = currentYear;
    
    if (newMonth > 12) {
      newMonth = 1;
      newYear++;
    } else if (newMonth < 1) {
      newMonth = 12;
      newYear--;
    }
    
    setCurrentMonth(newMonth);
    setCurrentYear(newYear);
  };

  const handleAddToCajaChica = async () => {
    const amount = parseFloat(addCajaChicaAmount);
    if (!amount || amount <= 0) {
      return toast.error('Ingresa un monto válido');
    }

    if (addCajaChicaSource === 'caja_ventas' && amount > cajaVentas.balance) {
      return toast.error('Fondos insuficientes en Caja de Ventas');
    }

    try {
      await api.post(`/finances/caja-chica/add?amount=${amount}&source=${addCajaChicaSource}`);
      toast.success(`Se agregaron $${amount.toLocaleString()} a Caja Chica`);
      setShowAddCajaChicaModal(false);
      setAddCajaChicaAmount('');
      setAddCajaChicaSource('manual');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al agregar fondos');
    }
  };

  const getSelectedCategory = () => {
    return categories.find(c => c.id === form.category);
  };

  const getCategoryIcon = (catId) => {
    const Icon = CATEGORY_ICONS[catId] || MoreHorizontal;
    return Icon;
  };

  // Debug para ver datos
  useEffect(() => {
    if (summary) {
      console.log('Summary data:', summary);
    }
  }, [summary]);

  const months = ['', 'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 
                  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

  return (
    <div className="min-h-screen bg-background">
      <Sidebar />
      <main className="flex-1 md:ml-64 p-4 md:p-8 pt-20 md:pt-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-chivo font-extrabold">Gastos Operativos</h1>
            <p className="text-muted-foreground">Control de pagos reales vs presupuesto</p>
          </div>
          <Button onClick={() => openModal()} className="bg-accent hover:bg-accent/90" data-testid="new-egreso-btn">
            <Plus className="w-4 h-4 mr-2" />
            Registrar Pago
          </Button>
        </div>

        {/* Month Selector */}
        <div className="flex items-center justify-center gap-4 mb-6">
          <Button variant="outline" size="icon" onClick={() => changeMonth(-1)}>
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <div className="text-xl font-bold min-w-[200px] text-center">
            {months[currentMonth]} {currentYear}
          </div>
          <Button variant="outline" size="icon" onClick={() => changeMonth(1)}>
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent"></div>
          </div>
        ) : (
          <>
            {/* Summary Cards */}
            {summary && summary.summary && (
              <>
                {/* Cash Sources */}
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <Card className="bg-gradient-to-br from-amber-50 to-amber-100 border border-amber-200">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <PiggyBank className="w-5 h-5 text-amber-600" />
                          <p className="text-sm font-medium text-amber-700">Caja Chica</p>
                        </div>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="h-7 text-amber-600 hover:text-amber-700"
                          onClick={() => setShowAddCajaChicaModal(true)}
                        >
                          <Plus className="w-4 h-4 mr-1" />
                          Agregar
                        </Button>
                      </div>
                      <p className="text-2xl font-bold text-amber-800 mt-1">
                        ${cajaChica.balance?.toLocaleString() || '0'}
                      </p>
                    </CardContent>
                  </Card>
                  
                  <Card className={`border ${cajaVentas.isOpen ? 'bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-200' : 'bg-slate-50 border-slate-200'}`}>
                    <CardContent className="p-4">
                      <div className="flex items-center gap-2">
                        <Store className={`w-5 h-5 ${cajaVentas.isOpen ? 'text-emerald-600' : 'text-slate-400'}`} />
                        <p className={`text-sm font-medium ${cajaVentas.isOpen ? 'text-emerald-700' : 'text-slate-500'}`}>
                          Caja de Ventas
                        </p>
                        {!cajaVentas.isOpen && (
                          <span className="text-xs bg-slate-200 px-2 py-0.5 rounded">Cerrada</span>
                        )}
                      </div>
                      <p className={`text-2xl font-bold mt-1 ${cajaVentas.isOpen ? 'text-emerald-800' : 'text-slate-400'}`}>
                        ${cajaVentas.balance?.toLocaleString() || '0'}
                      </p>
                    </CardContent>
                  </Card>
                </div>

                {/* Budget vs Real Summary */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                <Card className="bg-blue-50 border border-blue-200">
                  <CardContent className="p-4">
                    <p className="text-sm text-blue-600 mb-1">Presupuesto</p>
                    <p className="text-2xl font-bold text-blue-700">
                      ${summary.summary.total_budget?.toLocaleString() || '0'}
                    </p>
                    <p className="text-xs text-blue-500">Gastos fijos mensuales</p>
                  </CardContent>
                </Card>
                
                <Card className={`border ${summary.summary.status === 'over' ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'}`}>
                  <CardContent className="p-4">
                    <p className={`text-sm mb-1 ${summary.summary.status === 'over' ? 'text-red-600' : 'text-green-600'}`}>
                      Pagado
                    </p>
                    <p className={`text-2xl font-bold ${summary.summary.status === 'over' ? 'text-red-700' : 'text-green-700'}`}>
                      ${summary.summary.total_real?.toLocaleString() || '0'}
                    </p>
                    <p className={`text-xs ${summary.summary.status === 'over' ? 'text-red-500' : 'text-green-500'}`}>
                      {summary.summary.payments_count || 0} pagos registrados
                    </p>
                  </CardContent>
                </Card>

                <Card className={`border ${summary.summary.difference >= 0 ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'}`}>
                  <CardContent className="p-4">
                    <p className={`text-sm mb-1 ${summary.summary.difference >= 0 ? 'text-emerald-600' : 'text-amber-600'}`}>
                      Diferencia
                    </p>
                    <p className={`text-2xl font-bold flex items-center gap-1 ${summary.summary.difference >= 0 ? 'text-emerald-700' : 'text-amber-700'}`}>
                      {summary.summary.difference >= 0 ? (
                        <TrendingDown className="w-5 h-5" />
                      ) : (
                        <TrendingUp className="w-5 h-5" />
                      )}
                      ${Math.abs(summary.summary.difference || 0).toLocaleString()}
                    </p>
                    <p className={`text-xs ${summary.summary.difference >= 0 ? 'text-emerald-500' : 'text-amber-500'}`}>
                      {summary.summary.difference >= 0 ? 'Ahorro' : 'Excedente'}
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-4">
                    <p className="text-sm text-muted-foreground mb-1">Avance</p>
                    <p className="text-2xl font-bold">{summary.summary.percentage || 0}%</p>
                    <div className="w-full bg-slate-200 rounded-full h-2 mt-2">
                      <div 
                        className={`h-2 rounded-full transition-all ${
                          (summary.summary.percentage || 0) > 100 ? 'bg-red-500' : 
                          (summary.summary.percentage || 0) > 80 ? 'bg-amber-500' : 'bg-green-500'
                        }`}
                        style={{ width: `${Math.min(summary.summary.percentage || 0, 100)}%` }}
                      />
                    </div>
                  </CardContent>
                </Card>
              </div>
              </>
            )}

            {/* Comparison by Category */}
            {summary && summary.by_category && summary.by_category.length > 0 && (
              <Card className="mb-6">
                <CardHeader>
                  <CardTitle className="text-lg">Presupuesto vs Real por Categoría</CardTitle>
                </CardHeader>
                <CardContent className="relative z-10">
                  <div className="divide-y">
                      {summary.by_category.map((cat, index) => {
                        const Icon = CATEGORY_ICONS[cat.category_id] || MoreHorizontal;
                        return (
                          <div key={cat.category_id || index} className="py-3 flex items-center">
                            <div className="flex items-center gap-3 flex-1">
                              <div 
                                className="w-10 h-10 rounded-full flex items-center justify-center"
                                style={{ backgroundColor: cat.color || '#f97316' }}
                              >
                                <Icon className="w-5 h-5 text-white" />
                              </div>
                              <div>
                                <span className="font-bold">{cat.category_name || 'Sin nombre'}</span>
                                <span className="text-sm text-muted-foreground ml-2">({cat.payments_count || 0} pagos)</span>
                              </div>
                            </div>
                            <div className="text-right">
                              <span className="text-lg font-bold text-green-600">${(cat.real || 0).toLocaleString()}</span>
                              <span className="text-muted-foreground text-sm"> / ${(cat.budget || 0).toLocaleString()}</span>
                              <p className={`text-sm ${(cat.difference || 0) >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                                {(cat.difference || 0) >= 0 ? 'Ahorras' : 'Excedes'} ${Math.abs(cat.difference || 0).toLocaleString()}
                              </p>
                            </div>
                          </div>
                        );
                      })}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Daily Chart */}
            {summary && summary.daily_chart && (
              <Card className="mb-6">
                <CardHeader>
                  <CardTitle className="text-lg">Gastos Diarios - {months[currentMonth]}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-48">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={summary.daily_chart}>
                        <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                        <YAxis tickFormatter={(v) => `$${v}`} tick={{ fontSize: 10 }} />
                        <Tooltip formatter={(v) => `$${v.toLocaleString()}`} />
                        <Bar dataKey="amount" name="Egreso" radius={[4, 4, 0, 0]}>
                          {summary.daily_chart.map((entry, index) => (
                            <Cell key={index} fill={entry.amount > 0 ? '#f97316' : '#e2e8f0'} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Recent Egresos List */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Pagos Recientes</CardTitle>
              </CardHeader>
              <CardContent>
                {summary?.recent_egresos?.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <DollarSign className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p>No hay pagos registrados este mes</p>
                    <Button 
                      variant="outline" 
                      className="mt-4"
                      onClick={() => openModal()}
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Registrar primer pago
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {summary?.recent_egresos?.map((egreso) => {
                      const Icon = getCategoryIcon(egreso.category);
                      return (
                        <div 
                          key={egreso.egreso_id}
                          className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
                              <Icon className="w-5 h-5 text-orange-600" />
                            </div>
                            <div>
                              <p className="font-medium">{egreso.description}</p>
                              <p className="text-xs text-muted-foreground">
                                {egreso.payment_date} • {egreso.payment_method}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className="font-bold text-lg">${egreso.amount.toLocaleString()}</span>
                            <div className="flex gap-1">
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={() => openModal(egreso)}
                              >
                                <Edit2 className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-red-500 hover:text-red-600"
                                onClick={() => handleDelete(egreso.egreso_id)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </main>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle>{editingEgreso ? 'Editar Egreso' : 'Registrar Pago'}</CardTitle>
                <Button variant="ghost" size="icon" onClick={() => setShowModal(false)}>
                  <X className="w-5 h-5" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Category */}
                <div>
                  <Label>Categoría *</Label>
                  <Select value={form.category} onValueChange={(v) => setForm({...form, category: v, subcategory: ''})}>
                    <SelectTrigger data-testid="egreso-category-select">
                      <SelectValue placeholder="Seleccionar categoría" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((cat) => {
                        const Icon = getCategoryIcon(cat.id);
                        return (
                          <SelectItem key={cat.id} value={cat.id}>
                            <div className="flex items-center gap-2">
                              <Icon className="w-4 h-4" style={{ color: cat.color }} />
                              {cat.name}
                            </div>
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>

                {/* Subcategory for Servicios */}
                {form.category === 'servicios' && (
                  <div>
                    <Label>Tipo de Servicio</Label>
                    <Select value={form.subcategory} onValueChange={(v) => setForm({...form, subcategory: v})}>
                      <SelectTrigger>
                        <SelectValue placeholder="Seleccionar servicio" />
                      </SelectTrigger>
                      <SelectContent>
                        {getSelectedCategory()?.subcategories?.map((sub) => (
                          <SelectItem key={sub} value={sub}>{sub}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* Description */}
                <div>
                  <Label>Descripción *</Label>
                  <Input
                    value={form.description}
                    onChange={(e) => setForm({...form, description: e.target.value})}
                    placeholder={
                      form.category === 'nomina' ? 'Ej: Nómina Juan Pérez' :
                      form.category === 'servicios' ? 'Ej: Pago CFE Febrero' :
                      form.category === 'renta' ? 'Ej: Renta local Febrero' :
                      'Descripción del pago'
                    }
                    data-testid="egreso-description-input"
                  />
                </div>

                {/* Amount and Date */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Monto *</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={form.amount}
                      onChange={(e) => setForm({...form, amount: e.target.value})}
                      placeholder="0.00"
                      data-testid="egreso-amount-input"
                    />
                  </div>
                  <div>
                    <Label>Fecha de Pago *</Label>
                    <Input
                      type="date"
                      value={form.payment_date}
                      onChange={(e) => setForm({...form, payment_date: e.target.value})}
                      data-testid="egreso-date-input"
                    />
                  </div>
                </div>

                {/* Payment Method */}
                <div>
                  <Label>Método de Pago</Label>
                  <div className="flex gap-2 mt-1">
                    {PAYMENT_METHODS.map((method) => {
                      const Icon = method.icon;
                      return (
                        <Button
                          key={method.id}
                          type="button"
                          variant={form.payment_method === method.id ? 'default' : 'outline'}
                          className={`flex-1 ${form.payment_method === method.id ? 'bg-accent' : ''}`}
                          onClick={() => setForm({...form, payment_method: method.id})}
                        >
                          <Icon className="w-4 h-4 mr-1" />
                          {method.name}
                        </Button>
                      );
                    })}
                  </div>
                </div>

                {/* Money Source - Only for cash payments */}
                {form.payment_method === 'efectivo' && (
                  <div>
                    <Label className="flex items-center gap-2">
                      <Wallet className="w-4 h-4" />
                      ¿De dónde sale el dinero?
                    </Label>
                    <div className="grid grid-cols-2 gap-3 mt-2">
                      {MONEY_SOURCES.map((source) => {
                        const Icon = source.icon;
                        const isSelected = form.money_source === source.id;
                        const balance = source.id === 'caja_chica' ? cajaChica.balance : cajaVentas.balance;
                        const isAvailable = source.id === 'caja_ventas' ? cajaVentas.isOpen : true;
                        
                        return (
                          <button
                            key={source.id}
                            type="button"
                            disabled={!isAvailable}
                            onClick={() => setForm({...form, money_source: source.id})}
                            className={`p-3 rounded-lg border-2 transition-all text-left ${
                              isSelected 
                                ? 'border-accent bg-accent/10' 
                                : 'border-slate-200 hover:border-slate-300'
                            } ${!isAvailable ? 'opacity-50 cursor-not-allowed' : ''}`}
                            data-testid={`source-${source.id}`}
                          >
                            <div className="flex items-center gap-2 mb-1">
                              <Icon className={`w-5 h-5 ${isSelected ? 'text-accent' : 'text-slate-500'}`} />
                              <span className={`font-medium ${isSelected ? 'text-accent' : ''}`}>
                                {source.name}
                              </span>
                            </div>
                            <p className="text-xs text-muted-foreground mb-1">{source.description}</p>
                            <p className={`text-sm font-bold ${balance > 0 ? 'text-green-600' : 'text-red-500'}`}>
                              Disponible: ${balance?.toLocaleString()}
                            </p>
                            {source.id === 'caja_ventas' && cajaVentas.isOpen && (
                              <div className="text-xs text-slate-500 mt-1 space-y-0.5">
                                <p>Fondo: ${cajaVentas.initialAmount?.toLocaleString() || 0}</p>
                                <p className="text-green-600">+ Ventas: ${cajaVentas.cashSales?.toLocaleString() || 0}</p>
                                {cajaVentas.compras > 0 && (
                                  <p className="text-red-500">- Compras: ${cajaVentas.compras?.toLocaleString() || 0}</p>
                                )}
                                {cajaVentas.gastosOperativos > 0 && (
                                  <p className="text-orange-500">- Gastos Op: ${cajaVentas.gastosOperativos?.toLocaleString() || 0}</p>
                                )}
                                {cajaVentas.otrosSalidas > 0 && (
                                  <p className="text-slate-500">- Otros: ${cajaVentas.otrosSalidas?.toLocaleString() || 0}</p>
                                )}
                              </div>
                            )}
                            {source.id === 'caja_ventas' && !cajaVentas.isOpen && (
                              <p className="text-xs text-red-500 mt-1">Caja cerrada</p>
                            )}
                          </button>
                        );
                      })}
                    </div>
                    {form.amount && parseFloat(form.amount) > 0 && (
                      <div className={`mt-2 p-2 rounded text-sm ${
                        (form.money_source === 'caja_chica' && parseFloat(form.amount) > cajaChica.balance) ||
                        (form.money_source === 'caja_ventas' && parseFloat(form.amount) > cajaVentas.balance)
                          ? 'bg-red-50 text-red-700 border border-red-200'
                          : 'bg-green-50 text-green-700 border border-green-200'
                      }`}>
                        {(form.money_source === 'caja_chica' && parseFloat(form.amount) > cajaChica.balance) ||
                         (form.money_source === 'caja_ventas' && parseFloat(form.amount) > cajaVentas.balance)
                          ? <><AlertTriangle className="w-4 h-4 inline mr-1" /> Fondos insuficientes en {form.money_source === 'caja_chica' ? 'Caja Chica' : 'Caja de Ventas'}</>
                          : <><CheckCircle className="w-4 h-4 inline mr-1" /> Se descontarán ${parseFloat(form.amount).toLocaleString()} de {form.money_source === 'caja_chica' ? 'Caja Chica' : 'Caja de Ventas'}</>
                        }
                      </div>
                    )}
                  </div>
                )}

                {/* Reference */}
                <div>
                  <Label>Referencia / Factura (opcional)</Label>
                  <Input
                    value={form.reference}
                    onChange={(e) => setForm({...form, reference: e.target.value})}
                    placeholder="Número de recibo o factura"
                  />
                </div>

                {/* Notes */}
                <div>
                  <Label>Notas (opcional)</Label>
                  <Input
                    value={form.notes}
                    onChange={(e) => setForm({...form, notes: e.target.value})}
                    placeholder="Notas adicionales"
                  />
                </div>

                <Button type="submit" className="w-full bg-accent hover:bg-accent/90" data-testid="save-egreso-btn">
                  {editingEgreso ? 'Actualizar' : 'Registrar'} Pago
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Modal Agregar a Caja Chica */}
      {showAddCajaChicaModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <PiggyBank className="w-5 h-5 text-amber-600" />
                  Agregar a Caja Chica
                </CardTitle>
                <Button variant="ghost" size="icon" onClick={() => setShowAddCajaChicaModal(false)}>
                  <X className="w-5 h-5" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <Label>Monto a agregar *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={addCajaChicaAmount}
                    onChange={(e) => setAddCajaChicaAmount(e.target.value)}
                    placeholder="0.00"
                    data-testid="add-caja-chica-amount"
                  />
                </div>
                
                <div>
                  <Label>Origen del dinero</Label>
                  <div className="grid grid-cols-2 gap-3 mt-2">
                    <button
                      type="button"
                      onClick={() => setAddCajaChicaSource('manual')}
                      className={`p-3 rounded-lg border-2 transition-all text-left ${
                        addCajaChicaSource === 'manual' 
                          ? 'border-accent bg-accent/10' 
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <Banknote className={`w-5 h-5 mb-1 ${addCajaChicaSource === 'manual' ? 'text-accent' : 'text-slate-500'}`} />
                      <p className="font-medium text-sm">Efectivo Externo</p>
                      <p className="text-xs text-muted-foreground">Dinero de otra fuente</p>
                    </button>
                    <button
                      type="button"
                      onClick={() => setAddCajaChicaSource('caja_ventas')}
                      disabled={!cajaVentas.isOpen}
                      className={`p-3 rounded-lg border-2 transition-all text-left ${
                        addCajaChicaSource === 'caja_ventas' 
                          ? 'border-accent bg-accent/10' 
                          : 'border-slate-200 hover:border-slate-300'
                      } ${!cajaVentas.isOpen ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <Store className={`w-5 h-5 mb-1 ${addCajaChicaSource === 'caja_ventas' ? 'text-accent' : 'text-slate-500'}`} />
                      <p className="font-medium text-sm">Caja de Ventas</p>
                      <p className="text-xs text-muted-foreground">
                        {cajaVentas.isOpen ? `Disponible: $${cajaVentas.balance.toLocaleString()}` : 'Caja cerrada'}
                      </p>
                    </button>
                  </div>
                </div>

                {addCajaChicaSource === 'caja_ventas' && addCajaChicaAmount && parseFloat(addCajaChicaAmount) > cajaVentas.balance && (
                  <div className="p-2 bg-red-50 border border-red-200 rounded text-sm text-red-700">
                    <AlertTriangle className="w-4 h-4 inline mr-1" />
                    Fondos insuficientes en Caja de Ventas
                  </div>
                )}

                <Button 
                  onClick={handleAddToCajaChica} 
                  className="w-full bg-amber-500 hover:bg-amber-600"
                  disabled={!addCajaChicaAmount || parseFloat(addCajaChicaAmount) <= 0}
                  data-testid="confirm-add-caja-chica"
                >
                  Agregar ${parseFloat(addCajaChicaAmount || 0).toLocaleString()} a Caja Chica
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default Egresos;
